import {
  Grid,
  IconButton,
  InputAdornment,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from '@mui/material';
import { memo, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from 'react-query';
import { NumericFormat } from 'react-number-format';
import { useFormik } from 'formik';
import * as yup from 'yup';

import useTopPanel from 'hooks/useTopPanel';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useFormSubmit from 'hooks/useFormSubmit';
import FormActionButtons from 'components/ui/form/FormActionButtons';
import FormSelectField from 'components/ui/form/FormSelectField';
import FormDateField from 'components/ui/form/FormDateField';
import FormSimpleSelectField from 'components/ui/form/FormSimpleSelectField';
import FormTextField from 'components/ui/form/FormTextField';
import moment from 'moment';

const validationSchema = yup.object({
  master_id: yup.number().required('Masterni tanlanishi majburiy!'),
  // reason_id: yup.number().required('Tanlanish majburiy!'),
  type: yup.number().required('Turni tanlanish majburiy!'),
  date: yup
    .date()
    .typeError("Sana noto'g'ri kiritilgan!")
    .required('Sana kiritish majburiy!'),
});

const types = [
  {
    name: 'Obyekt',
    id: 1,
  },
  {
    name: 'Blok',
    id: 2,
  },
  {
    name: 'Hamkor',
    id: 3,
  },
];

const WarehouseInbound = () => {
  const { setComponent } = useTopPanel();
  const navigate = useNavigate();
  const axiosPrivate = useAxiosPrivate();
  const [hasError, setHasError] = useState(false);
  const [products, setProducts] = useState([]);
  const [filter, setFilter] = useState('');
  const { submit, isSubmitting } = useFormSubmit();
  const { id } = useParams();

  useEffect(() => {
    setComponent(
      <div className='component-title'>Mahsulotlarni ombordan chiqarish</div>
    );
  }, []);

  const formik = useFormik({
    initialValues: {
      reason_id: '',
      object_id: '',
      type: '',
      date: moment().format('YYYY-MM-DD'),
      master_id: '',
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      const newValues = {
        ...values,
        products: products
          ?.filter((pr) => pr?.amount)
          ?.map(({ product_id, amount }) => ({
            product_id,
            amount,
          })),
      };

      submit(
        { type: 'post', contentType: 'simple' },
        newValues,
        `/admin/warehouses/outcome/${id}`,
        'Ombordan mahsulot chiqarildi!',
        null,
        true,
        handleClose
      );
    },
  });

  const handleClose = () => {
    navigate('/admin/warehouse');
    formik.resetForm();
  };

  const { isLoading: isLoadingById, isFetching: isFetchingById } = useQuery({
    queryKey: ['dictionary/warehouseproducts', id],
    queryFn: async function () {
      const response = await axiosPrivate.get(
        `dictionary/warehouseproducts/${id}`
      );
      return response.data.data;
    },
    enabled: !hasError && !!id,
    onSuccess: (data) => {
      if (data?.length > 0) {
        const dataList = data?.map((pr) => ({
          ...pr,
          name: pr?.product?.name,
          amount: '',
          max_amount: Number?.(pr?.amount) || 0,
        }));
        setProducts(dataList);
      }
    },
    onError: (error) => {
      setHasError(true);
    },
    retry: false,
  });

  const setValue = (id, field, value) => {
    setProducts((prev) =>
      prev?.map((pr) => (pr?.id === id ? { ...pr, [field]: value } : pr))
    );
  };

  const filteredData = useMemo(() => {
    if (products?.length) {
      try {
        if (filter)
          return products?.filter((p) =>
            p?.name?.toUpperCase?.()?.includes?.(filter?.toUpperCase?.())
          );
        else return products;
      } catch {
        return [];
      }
    } else return [];
  }, [products, filter]);

  useEffect(() => {
    formik.setFieldValue('reason_id', '');
    formik.setFieldValue('object_id', null);
  }, [formik.values.type]);

  return (
    <div className='component-list-wrapper'>
      <div className='component-table-wrapper h-[calc(100vh-80px)]'>
        <form onSubmit={formik.handleSubmit}>
          <Grid
            container
            spacing={{ xs: 2, sm: 3, lg: 3 }}
            rowSpacing={1}
            columns={{ xs: 12, sm: 12, lg: 12 }}
          >
            <Grid item={true} lg={1.5} sm={2} xs={2}>
              <FormDateField label='Sana' fieldName='date' formik={formik} />
            </Grid>
            <Grid item={true} lg={1} sm={2} xs={2}>
              <FormSelectField
                delay={0}
                label={'Master'}
                fieldName='master_id'
                formik={formik}
                path={'/dictionary/masters'}
              />
            </Grid>
            <Grid item={true} lg={1} sm={2} xs={2}>
              <FormSimpleSelectField
                delay={0}
                label={'Turi'}
                fieldName='type'
                options={types}
                formik={formik}
              />
            </Grid>

            <Grid item={true} lg={8.5} sm={8} xs={8}>
              <div className='flex gap-4'>
                {formik?.values?.type == 3 && (
                  <Grid key={'reason_id'} item={true} lg={6} sm={6} xs={12}>
                    <FormSelectField
                      delay={0}
                      key={'reason_id'}
                      label={'Hamkor'}
                      fieldName={'reason_id'}
                      formik={formik}
                      disabled={!formik?.values?.type}
                      path={'/dictionary/partners'}
                    />
                  </Grid>
                )}
                {formik?.values?.type != 3 && (
                  <Grid key={'object_id'} item={true} lg={6} sm={6} xs={12}>
                    <FormSelectField
                      delay={0}
                      key={'object_id'}
                      label={'Obyekt'}
                      fieldName={
                        formik?.values?.type == 2 ? 'object_id' : 'reason_id'
                      }
                      formik={formik}
                      disabled={!formik?.values?.type}
                      path={'/dictionary/objects'}
                    />
                  </Grid>
                )}
                {formik?.values?.type == 2 && (
                  <Grid key={'reason_id'} item={true} lg={6} sm={6} xs={12}>
                    <FormSelectField
                      key={'reason_id'}
                      delay={0}
                      label={'Blok'}
                      fieldName='reason_id'
                      disabled={!formik?.values?.object_id}
                      formik={formik}
                      path={`/dictionary/blocks/${formik?.values?.object_id}`}
                      pathChangeable
                    />
                  </Grid>
                )}
                <FormTextField
                  delay={0}
                  label={'izoh'}
                  fieldName='comment'
                  fullWidth
                  formik={formik}
                />
                <div className='[&>div]:flex [&>div]:flex-nowrap [&>div]:!mt-2.5 whitespace-nowrap'>
                  <FormActionButtons
                    delay={0}
                    isSubmitting={isSubmitting}
                    disabled={!products?.some((pr) => pr?.amount > 0)}
                    formType='dialog'
                    setOpen={handleClose}
                    reset={formik.resetForm}
                  />
                </div>
              </div>
            </Grid>
          </Grid>
        </form>
        <div className='grow'>
          <TableContainer
            className='tab-table-container !shadow-none !base-border h-[calc(100vh-140px)]'
            component='div'
          >
            <Table stickyHeader component='div'>
              <TableHead component='div'>
                <TableRow component='div'>
                  <TableCell component='div' className='!bg-gray-50 !w-[80px]'>
                    #
                  </TableCell>
                  <TableCell component='div' className='!bg-gray-50 !w-[550px]'>
                    <div className='flex items-center gap-2'>
                      <span>Nomi</span>
                      <TextField
                        className='!m-0'
                        size='small'
                        placeholder='Qidiruv'
                        value={filter}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment
                              position='end'
                              className='custom-endAdornment'
                            >
                              {filter ? (
                                <IconButton
                                  onClick={() => setFilter('')}
                                  variant='onlyIcon'
                                >
                                  <i className='bi bi-x end-adornment-close-button' />
                                </IconButton>
                              ) : (
                                <IconButton
                                  variant='onlyIcon'
                                  disabled
                                  disableRipple
                                >
                                  <i className='bi bi-search text-base leading-4' />
                                </IconButton>
                              )}
                            </InputAdornment>
                          ),
                        }}
                        fullWidth
                        onChange={(e) => setFilter(e?.target?.value)}
                      />
                    </div>
                  </TableCell>
                  <TableCell component='div' className='!bg-gray-50'>
                    Mavjud
                  </TableCell>
                  <TableCell component='div' className='!bg-gray-50'>
                    Miqdori
                  </TableCell>
                </TableRow>
              </TableHead>
              {isLoadingById || isFetchingById ? (
                <TableBody className='overflow-hidden' component='div'>
                  <TableRow component='div'>
                    <TableCell colSpan={6}>
                      <LinearProgress />
                    </TableCell>
                  </TableRow>
                </TableBody>
              ) : filteredData?.length > 0 ? (
                <TableBody
                  className='overflow-hidden !bg-white'
                  component='div'
                >
                  {filteredData?.map((row, rowIndex) => {
                    return (
                      <TableRow
                        hover
                        tabIndex={-1}
                        key={'row-' + rowIndex}
                        className='no-underline'
                      >
                        <TableCell size='small' component='div'>
                          {rowIndex + 1}
                        </TableCell>
                        <TableCell size='small' component='div'>
                          {row?.name || ''}
                        </TableCell>
                        <TableCell size='small' component='div'>
                          {row?.max_amount || ''}
                        </TableCell>
                        <TableCell size='small' component='div'>
                          <NumericFormat
                            onChange={(event) => {
                              let formattedValue =
                                event.target.value &&
                                parseFloat(
                                  event.target.value.split(' ').join('')
                                );

                              setValue(row?.id, 'amount', formattedValue);
                            }}
                            fullWidth
                            value={Number(row?.amount) || ''}
                            isAllowed={(values) => {
                              const { floatValue } = values;
                              return (
                                (floatValue || 0) <= Number?.(row?.max_amount)
                              );
                            }}
                            InputProps={{
                              endAdornment: (
                                <InputAdornment
                                  position='end'
                                  className='custom-endAdornment'
                                >
                                  {id ? (
                                    ''
                                  ) : (
                                    <span className='pr-1'>
                                      max: {row?.max_amount}
                                    </span>
                                  )}
                                </InputAdornment>
                              ),
                            }}
                            customInput={TextField}
                            allowNegative={false}
                            max={row?.max_amount}
                            thousandSeparator={' '}
                            sx={{ m: 0, minWidth: 180 }}
                            decimalScale={3}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              ) : (
                <TableBody component='div'>
                  <TableRow component='div'>
                    <TableCell colSpan={6}>
                      <span className='text-gray-400 flex items-center justify-center'>
                        <i className='bi bi-exclamation-octagon text-xl leading-4 mr-1' />{' '}
                        Ma'lumot topilmadi!
                      </span>
                    </TableCell>
                  </TableRow>
                </TableBody>
              )}
            </Table>
          </TableContainer>
        </div>
      </div>
    </div>
  );
};

export default memo(WarehouseInbound);
